import useToast from 'application/hooks/use-toast';
import useTranslate from 'application/hooks/use-translate';
import ReportService from 'application/services/report-service';
import downloadFile from 'application/utilities/download-file';
import YearSelector from 'application/pages/groups/group/components/year-selector';
import React, { useState } from 'react';
import MonthSelector from 'application/pages/groups/group/components/month-selector';
import DownloadButton from 'application/components/download-button';
import LoadingState from 'application/pages/groups/group/components/reports/loading-state';
import HTMLReport from 'application/pages/groups/group/components/reports/html-report';
import NoDataAvailableState from 'application/pages/groups/group/components/reports/no-data-available-state';
import { useQuery } from '@tanstack/react-query';
import Icon from 'application/components/icon';

interface Props {
  groupId: string;
}

const GroupMonthlyIncomeReport = ({ groupId }: Props) => {
  const translate = useTranslate('reports');
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const { data, isFetching, isSuccess } = useQuery({
    queryKey: ['group-monthly-income-report', selectedMonth, selectedYear],
    queryFn: () => {
      if (!selectedMonth || !selectedYear) {
        return Promise.reject();
      }

      return ReportService.fetchGroupMonthlyIncomeReport(groupId, selectedMonth, selectedYear).catch(() => {
        addToast({
          message: translate('error-downloading-report'),
          type: 'error'
        });
      });
    },
    enabled: !!selectedMonth && !!selectedYear
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const { addToast } = useToast();

  const handleDownloadPromise = (response: Promise<Response>) => {
    response
      .then((response) => {
        if (response.status === 404) {
          addToast({
            message: translate('no-data-available'),
            type: 'warning'
          });
        }

        if (!response.ok) {
          addToast({
            message: translate('error-downloading-report'),
            type: 'error'
          });
        }

        downloadFile(response);
      })
      .catch(() => {
        addToast({
          message: translate('error-downloading-report'),
          type: 'error'
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const fetchFile = () => {
    if (!selectedMonth || !selectedYear) return;

    setIsDownloading(true);

    return handleDownloadPromise(ReportService.downloadGroupMonthlyIncomeReport(groupId, selectedMonth, selectedYear));
  };

  const exportFile = () => {
    if (!selectedMonth || !selectedYear) return;

    setIsDownloading(true);

    return handleDownloadPromise(ReportService.exportGroupMonthlyIncomeReport(groupId, selectedMonth, selectedYear));
  };

  return (
    <>
      <div className="absolute right-4 top-4 flex items-center print:hidden">
        <MonthSelector onChange={setSelectedMonth} value={selectedMonth} />
        <YearSelector onChange={setSelectedYear} value={selectedYear} />
        <DownloadButton
          disabled={!data || !isSuccess || isFetching}
          downloading={isDownloading}
          modes={['pdf', 'csv']}
          onClick={(mode) => {
            if (mode === 'pdf') {
              return fetchFile();
            }

            if (mode === 'csv') {
              return exportFile();
            }
          }}
        />
      </div>

      {isFetching && <LoadingState />}

      {data && isSuccess && !isFetching ? <HTMLReport report={data} /> : null}

      {!isFetching && !data && isSuccess && <NoDataAvailableState />}

      {!isFetching && !data && !isSuccess && (
        <div className="flex h-96 flex-col items-center justify-center gap-4 bg-base-200">
          <Icon icon="calendar-range" className="text-4xl text-gray-400" />

          <span className="w-64 text-center text-gray-400">{translate('select-billing-period')}</span>
        </div>
      )}
    </>
  );
};

export default GroupMonthlyIncomeReport;
